import React, { useEffect, useState } from "react";
import { useApi } from "../hooks/useApi";
import { formatUtcDateTime } from "../utils/dateTime";

/**
 * Step 8: what an account did on-premises in the activity window, from the Defender for Identity
 * event tables (aggregates — no raw events are stored).
 *
 * Three emptiness states, kept apart the way step 7 does it: no sensor reporting (unknown), read
 * and nothing in the window (quiet), and never read. "We could not look" is never shown as
 * "this account did nothing", because dormancy conversations turn on exactly that difference.
 */
export default function IdentityActivity({ id }) {
  const apiCall = useApi();
  const [data, setData] = useState(null); // null = loading

  useEffect(() => {
    let cancelled = false;
    apiCall("GET", `/defender-identity/identities/${id}/activity`)
      .then(r => { if (!cancelled) setData(r && typeof r.state === "string" ? r : { activity: null, state: "notRead" }); })
      .catch(() => { if (!cancelled) setData({ activity: null, state: "notRead" }); });
    return () => { cancelled = true; };
  }, [apiCall, id]);

  const a = data?.activity ?? null;
  const windowDays = a?.windowDays ?? 30;
  const Fact = ({ label, children }) => (
    <div className="flex items-baseline justify-between gap-2 py-1 border-b border-gray-100 text-xs">
      <span className="text-gray-500">{label}</span>
      <span className="text-gray-800 text-right">{children}</span>
    </div>
  );

  return (
    <>
      <div className="text-[11px] uppercase tracking-wide text-gray-400 mt-4 mb-1">Activity ({windowDays} days)</div>
      {data === null && <div className="text-xs text-gray-400 py-1.5">Loading…</div>}

      {data && data.state === "unavailable" && (
        <div className="text-xs text-amber-700 py-1.5" data-testid="activity-unavailable">
          Not known: {data.message || "on-premises activity could not be read."}
        </div>
      )}
      {data && data.state === "notRead" && (
        <div className="text-xs text-gray-500 py-1.5" data-testid="activity-not-read">
          Not known: no sync has read on-premises activity yet.
        </div>
      )}
      {data && data.state === "available" && !a && (
        <div className="text-xs text-gray-400 py-1.5" data-testid="activity-quiet">
          No on-premises activity observed for this account in the last {windowDays} days.
        </div>
      )}

      {a && (
        <div data-testid="identity-activity">
          {/* The card may be readable for some tables and not others; saying which keeps the
              numbers honest rather than implying the window was fully observed. */}
          {data.state === "available" && data.message && (
            <div className="text-[11px] text-amber-700 py-1" data-testid="activity-partial">Partly observed: {data.message}</div>
          )}
          <Fact label="Last activity">{a.lastActivityAt ? formatUtcDateTime(a.lastActivityAt) : "—"}</Fact>
          <Fact label="Authentications">
            {a.logons}
            {a.failedLogons > 0 && <span className="text-gray-500"> · {a.failedLogons} failed</span>}
          </Fact>
          <Fact label="Devices authenticated to">{a.distinctDevices}</Fact>
          <Fact label="Directory changes made">
            {a.directoryChanges}
            {a.sensitiveGroupChanges > 0 && (
              <span className="ml-1 px-1 rounded bg-amber-50 text-amber-700 border border-amber-200">{a.sensitiveGroupChanges} group change{a.sensitiveGroupChanges === 1 ? "" : "s"}</span>
            )}
          </Fact>
          {a.directoryActions && a.directoryActions.length > 0 && (
            <div className="text-[11px] text-gray-500 py-1 break-all" data-testid="activity-actions">{a.directoryActions.join(", ")}</div>
          )}
          <Fact label="Directory queries run">
            {a.queries}
            {a.distinctQueryTargets > 0 && <span className="text-gray-500"> · {a.distinctQueryTargets} distinct target{a.distinctQueryTargets === 1 ? "" : "s"}</span>}
          </Fact>
          {!data.inLastSync && (
            <div className="text-[11px] text-amber-700">
              This summary is from an earlier read
              {data.lastSeenAt ? ` (${formatUtcDateTime(data.lastSeenAt)})` : ""} — the latest sync did not report activity for this account.
            </div>
          )}
        </div>
      )}
    </>
  );
}
